import { v4 as uuidv4 } from "uuid";
import * as machinemotion from "../api/machinemotion";
import { configCheckerResultStates } from "./constants";
import { Notification } from "./Notification";
import { axisModel, nonMotorizedAxisModel } from "@ventionco/mm-model";
import { action, computed, makeObservable, observable } from "mobx";

export function Axis(pConfig, pAxisData) {
  Notification.call(this);

  const axisSchemaParameters = axisModel.schemaMap.parameters;

  this.mUUID          = '';
  this.mConfig        = pConfig;
  this.mName          = '';
  this.mType          = axisSchemaParameters.axisType.default;
  this.mGain          = axisSchemaParameters.mmPerRotation.default;
  this.mMultiplier    = axisSchemaParameters.multiplier.default;
  // default for V1
  this.mTuningProfile = axisSchemaParameters.tuningProfile.default;
  this.mControlLoop   = axisSchemaParameters.controlLoop?.default || 'open';
  this.mBrake         = axisSchemaParameters.brake.default;
  this.mMotorSize     = axisSchemaParameters.motorSize.default;
  this.mMotorCurrent  = axisModel.getMotorSettings(this.mMotorSize).motorCurrent.default;
  this.mRotation      = axisSchemaParameters.rotation.default;
  this.mHomingSpeed   = axisSchemaParameters.homingSpeed.axisTypeMapping[this.mType];

  this.mColor = ''; // this is also the parent
  this.mChildrenDrives = [];


  //config checker
  this.mConfigCheckerResult = configCheckerResultStates.UNKNOWN;

  // pneumatics
  this.mDevice = 1;
  this.mPins = {
    pushOutPin: 0,
    pushInPin: null,
    pullOutPin: 1,
    pullInPin: null,
  };

  this.fromJson(pAxisData);

  if(!this.mColor && !this.isNonMotorized()) { console.error('Created Axis has no color on creation, drive instruction will not be build correctly.'); }

  makeObservable(this, {
    mName: observable,
    name: computed,
    unit: computed,
    type: computed,
    setName: action,
    setType: action,
    fromJson: action,
  });
}

Axis.prototype = Object.create(Notification.prototype);
Axis.prototype.constructor = Axis;

Axis.prototype.setDirty = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/config3d.js)
  this.mConfig.setDirty();
};

Axis.prototype.getConfigManager = function() {
  return this.mConfig.getConfigManager();
};

Axis.prototype.getUUID = function() { return this.mUUID; };

Axis.prototype.getName = function() { return this.mName; };

Axis.prototype.setName = function(pName) {
  if (this.mName === pName) {
    return;
  }

  const _lNewName = this.mConfig.uniqueAxisName(pName);
  this.mName = _lNewName;
  this.setDirty();
  this.notify('onNameChanged', this.mName);
  this.mConfig.notify('onAxisNameChanged',  this);
};

Axis.prototype.isConfigCheckerCompatible = function() {
  const lCompatibleTypes = axisModel.getAxisTypesCompatibleWithConfigChecker();
  return lCompatibleTypes.includes(this.getType());
};

Axis.prototype.updateConfigCheckerResultStatus = function () {
  machinemotion.getResultStatus(this.getControllerId(), this.getUUID(), (pStatus) => {
    // pStatus might not return a hasConfigChanged key. If that's the case, then set it to null
    const lIsConfigCheckerResultInvalid = pStatus === null ? null : pStatus.hasConfigChanged;
    let lConfigCheckerResult;

    // config the config has changed
    if(lIsConfigCheckerResultInvalid === true) { lConfigCheckerResult = configCheckerResultStates.INVALID; }

    // the config has not be changed
    if(lIsConfigCheckerResultInvalid === false) { lConfigCheckerResult = configCheckerResultStates.VALID; }

    // we have not run the config checker on this actuator yet.
    if(lIsConfigCheckerResultInvalid === null) { lConfigCheckerResult = configCheckerResultStates.UNCHECKED; }

    if(this.mConfigCheckerResult !== lConfigCheckerResult) {
      this.mConfigCheckerResult = lConfigCheckerResult;
      this.notify('onConfigCheckerResultChanged');
    }
  });
};

Axis.prototype.getConfigCheckerResult = function() {
  if (this.mConfigCheckerResult === configCheckerResultStates.UNKNOWN) { this.updateConfigCheckerResultStatus(); }
  return this.mConfigCheckerResult;
};

/**
 * Returns the Controller that this axis belongs to.
 * @returns {String} Unique ID of the controller.
 */
Axis.prototype.getControllerId = function() { return this.mConfig.getController().getUUID(); };

/**
 * Predicate to check if the actuator is not motorized (not controlled by the drives, coudld be pneumatics)
 * @returns {Boolean}
 */
Axis.prototype.isNonMotorized = function () {
  return this.mConfig.isAxisNonMotorized(this.mType);
};

Axis.prototype.isBagGripper = function() {
  return /pneumatic_bag_gripper/i.test(this.mType);
};

Axis.prototype.getDeviceRange = function () {
  return nonMotorizedAxisModel.schemaMap.parameters.device.values;
};

Axis.prototype.getPinRange = function () {
  return nonMotorizedAxisModel.schemaMap.parameters.pushOutPin.values;
};

/**
 * Get actuator label for axis config cad in cad
 * @returns {String} - 'PNEUMATIC' | 'ACTUATOR' | 'AC MOTOR' if vfd
 */
Axis.prototype.getAxisLabel = function() {
  if (this.getType() === 'ac_motor_with_vfd') { return 'AC MOTOR'; }
  if (this.isNonMotorized()) { return 'PNEUMATIC'; }
  return 'ACTUATOR';
};

/**
 * Get actuator label for axis config cad in edge
 * Use 1 based index
 * @returns {String} - 'PNEUMATIC' | 'Drive 1|2|3|4' | 'Drive 1,2' if multi-drive' | 'AC MOTOR' if vfd
 */
Axis.prototype.getAxisDriveLabel = function() {
  if (this.getType() === 'ac_motor_with_vfd') { return 'AC MOTOR'; }
  if (this.isNonMotorized()) { return 'PNEUMATIC'; }
  const lDrives = this.listDrives();
  return `DRIVE${lDrives.length > 1 ? 'S' : ''} ${lDrives.join(',')}`;
};

/**
 * Returns a list of all of the drives used by this Axis.
 *
 * @returns {number[]} List of drives used by this axis in range [1, 4] for MMV2.
 */
Axis.prototype.listDrives = function() {
  const lDriveNumberList = [ this.getDriveNumber() ];
  if (this.isNonMotorized() || !this.isMultiDriveAxis()) {
    return lDriveNumberList;
  }

  // the first index will always be the parent.
  const lAxisColor = this.mConfig.mAxisColors;
  this.mChildrenDrives.forEach(function(pChildMotor) {
    lDriveNumberList.push(lAxisColor.indexOf(pChildMotor.color) + 1);
  });

  return lDriveNumberList;
};

/**
 * Adds a new child drive to the axis.
 *
 * @param {string | undefined} pColor A valid color
 * @param {number} pRotation The rotation direction of the axis
 * @returns {Object} A new child drive
 */
Axis.prototype.addChildDrive = function(pColor, pRotation) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic_axis3d.js)
  const lConfig = this.mConfig;
  const lGetColor = function() {
    if (!lConfig.mAxisColors.includes(pColor)) {
      console.warn('Unable to add child drive with color=' + pColor);
      pColor = undefined;
    }

    return pColor || lConfig.findUnusedColor();
  };

  const lChild = {
    uuid: uuidv4(),
    color: lGetColor(),
    rotation: pRotation || 1
  };

  this.mChildrenDrives.push(lChild);
  this.setDirty();
  return lChild;
};

/**
 * Removes a child drive provided the unique ID of that child.
 *
 * @param {String} pUuid UniqueID of the child
 * @returns {Boolean} True if removed, otherwise false.
 */
Axis.prototype.removeChildDrive = function(pUuid) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic_axis3d.js)
  const lChildIdx = this.mChildrenDrives.findIndex(function(pChild) { return pChild.uuid === pUuid; });
  if (lChildIdx >= 0) {
    this.mChildrenDrives.splice(lChildIdx, 1);
    this.setDirty();
    return true;
  }

  return false;
};

/**
 * Disassociates all child drives from the axis.
 */
Axis.prototype.clearChildDrives = function() {
  this.mChildrenDrives = [];
  this.setDirty();
};

/**
 * Retrieves the child drive with the corresponding color.
 *
 * @param {String} pColor A valid color from this.mConfig.mAxisColors
 * @returns {Object} Child drive object
 */
Axis.prototype._getChildDriveByColorName = function(pColor) {
  return this.mChildrenDrives.find(function(pChild) {
    return pChild.color == pColor;
  });
};

/**
 * Retrieves the rotation and parent status of the drive.
 * Returns null if the drive does not belong to the actuator.
 *
 * @param {Number} pColorIndex Drive number in range [0, 3]
 * @returns {Object} Direction and parent status of drive.
 */
Axis.prototype.getAxisDriveInfo = function(pColorIndex) {
  const lColor = this.mConfig.mAxisColors[pColorIndex];
  const lChild = this._getChildDriveByColorName(lColor);

  if (lColor != this.mColor && !lChild) {
    return null;
  }

  return {
    direction: lChild ? lChild.rotation : this.mRotation,
    isParent: lColor == this.mColor,
  };
};

Axis.prototype.setColor = function(pColor) {
  if(pColor == this.mColor) { return; }

  this.mColor = pColor;
  this.setDirty();
  this.notify('onColorChanged', this.mColor);
};

Axis.prototype.getColor = function() {
  return this.mColor;
};

Axis.prototype.isMultiDriveAxis = function() {
  return this.mChildrenDrives.length >= 1;
};

Axis.prototype.getDriveNumber = function() {
  const lThis = this;
  let lIndex;

  if (this.isNonMotorized()) {
    lIndex = this.mConfig.listAxes().findIndex(function(lAxis) {
      return lAxis.getUUID() === lThis.getUUID();
    });
  }
  else {
    lIndex = this.mConfig.mAxisColors.indexOf(this.getColor());
  }

  return lIndex + 1; // Note (mkosarek) Add 1 to go from regular index to drive index.
};

Axis.prototype._adjustGain = function() {
  const gain = axisModel.getMmPerRotationForAxisType(this.getType());
  if (gain !== undefined)  {
    this.setGain(gain);
  }
};

Axis.prototype.getCompatibleTuningProfiles = function(){
  const tuningProfiles = ['default'];
  if(axisModel.isAxisTypeConveyorTurnTableCompatible(this.getType())) {
    tuningProfiles.push('conveyor_turntable');
  }

  // Custom tuning profile should only be set by pythonAPI. So only allow custom as a option only if it has already been set, remove it otherwise.
  if (this.getTuningProfile() == 'custom') {
    tuningProfiles.push('custom');
  }
  return tuningProfiles;
};

Axis.prototype.getCompatibleMotorSizes = function() {
  return axisModel.getCompatibleMotorSizesForAxisType(this.getType());
};

Axis.prototype.getCompatibleMotorCurrentInfo = function() {
  const lMotorCurrentInfo = axisModel.getMotorSettings(this.getMotorSize()).motorCurrent;
  if (!lMotorCurrentInfo) { return undefined; }
  return {
    min: lMotorCurrentInfo.minimum,
    max: lMotorCurrentInfo.maximum,
    info: "Default Value: " + lMotorCurrentInfo.default,
    default: lMotorCurrentInfo.default
  };
};

Axis.prototype._adjustGearbox = function(){
  // If the new type is not gearbox compatible, reinitialize the multiplier to one.
  if (!this.getGearboxCompatibility()){
    this.setMultiplier(1);
  }
};

Axis.prototype._adjustTuningProfile = function() {
  if (!axisModel.isAxisTypeConveyorTurnTableCompatible(this.getType())) {
    this.setTuningProfile(axisModel.schemaMap.parameters.tuningProfile.default);
  }
};

Axis.prototype._adjustMotorSize = function() {
  const lCompatibleMotorSizes = this.getCompatibleMotorSizes();
  if (!lCompatibleMotorSizes.includes(this.getMotorSize())) {
    const lDefaultMotorSize = axisModel.schemaMap.parameters.motorSize.default;
    if (lCompatibleMotorSizes.includes(lDefaultMotorSize)){ this.setMotorSize(lDefaultMotorSize); }
    else { this.setMotorSize(lCompatibleMotorSizes[0]); }
  }
};

Axis.prototype.clearPins = function() {
  Object.keys(this.mPins).forEach((pPinType) => this.mPins[pPinType] = undefined);
  this.mConfig.updateActuatorIOMap();
};

Axis.prototype.setType = function(pType) {
  // do nothing if same
  if (this.mType === pType) { return; }

  // Note (alevy): if transition from a non motorized actuator type to another type or vice-versa, i.e,
  // from any actuator type to a non motorized, we change colors.
  // This is because a non motorized actuator does not have a color, it is a pseudo-actuator.

  const pCurrentType = this.getType();
  this.clearPins();
  const _lResult = this.mConfig.checkForActuatorAddError(pType, pCurrentType);

  if(_lResult && _lResult.error) {
    this.notify('onTypeChanged', this.mType);
    return _lResult;
  }

  this.mType = pType;
  switch (pType){
    case 'ac_motor_with_vfd':
    case 'pneumatic': {
      this.setColor('');
      const lNextParams = this.mConfig.nextNonMotorizedAxisOutputs();
      this.setDevice(lNextParams.device);
      this.setPin('pushOutPin', lNextParams.pushOutPin);
      this.setPin('pullOutPin', lNextParams.pullOutPin);
      this.mChildrenDrives = [];
      break;
    }
    case 'pneumatic_bag_gripper': {
      this.setColor('');
      const lParams = this.mConfig.nextNonMotorizedAxisInputsAndOutputs();
      this.setDevice(lParams.device);
      this.setPin('pushOutPin', lParams.pushOutPin);
      this.setPin('pullOutPin', lParams.pullOutPin);
      this.setPin('pushInPin', lParams.pushInPin);
      this.setPin('pullInPin', lParams.pullInPin);
      this.mChildrenDrives = [];
      break;
    }
    default: {
      if (this.mColor == '') {
        const lUnusedColor = this.mConfig.findUnusedColor();
        this.setColor(lUnusedColor);
      }
      break;
    }
  }

  this.mConfig.updateActuatorIOMap();

  if (!this.isNonMotorized() && this.mType !== 'custom') {
    this._adjustTuningProfile();
    this._adjustGain();
    this._adjustGearbox();
    this._adjustMotorSize();
    if (!this.getBrakeCompatability()) { this.setBrake(false); }
    this._resetHomingSpeedToDefault();
  }
  // Note (alevy): keep not motorized actuators on the bottom of the actuator list
  this.mConfig.sortAxes();
  this.notify('onTypeChanged', this.mType);
  this.mConfig.notify('onAxisTypeChanged', this);
  this.setDirty();
};

Axis.prototype.getType = function() {
  return this.mType;
};

Axis.prototype.getUnits = function() {
  if (this.getType().includes("indexer")) { return "deg"; }
  return "mm";
};

Axis.prototype.setGain = function(pGain) {
  this.mGain = pGain;
  this.setDirty();
  this.notify('onGainChanged', this.mGain);
};

Axis.prototype.getGain = function() {
  return this.mGain;
};

Axis.prototype.setMultiplier = function(pMultiplier) {
  this.mMultiplier = pMultiplier;

  this.setDirty();
  this.notify('onMultiplierChanged', this.mMultiplier);
};

Axis.prototype.getMultiplier = function() {
  return this.mMultiplier;
};

Axis.prototype.multiplier2gearbox = function(pMultiplier) {
  return pMultiplier == 1 ? false : true;
};

Axis.prototype.gearbox2multiplier = function(pGearbox) {
  return pGearbox == false ? 1 : 1 / 5;
};

Axis.prototype.setTuningProfile = function(pTuningProfile) {
  this.mTuningProfile = pTuningProfile;
  this.setDirty();
  this.notify('onTuningProfileChanged', this.mTuningProfile);
};

Axis.prototype.getTuningProfile = function() {
  return this.mTuningProfile;
};
/**
 * get the default tuning profile for actuators
 * @returns {string} the default tuning profile
 */
Axis.prototype.getDefaultTuningProfile = function() {
  return axisModel.schemaMap.parameters.tuningProfile.default;
};

Axis.prototype.setMotorSize = function(pMotorSize) {
  this.mMotorSize = pMotorSize;
  // update motor current for new motor size
  this.setCurrent(this.getCompatibleMotorCurrentInfo().default);
  this.notify('onMotorSizeChange', this.mMotorSize);
  this.setDirty();
};

Axis.prototype.getMotorSize = function() {
  return this.mMotorSize;
};

Axis.prototype.setBrake = function(pBoolean) {
  this.mBrake = pBoolean ? "present" : "none";
  this.setDirty();
  this.notify('onBrakeChanged', this.getBrake());
};

Axis.prototype.getBrake = function() {
  return this.mBrake == "present" ? true : false;
};

// TODO: This prototype is just here for UI purposes and doesn't reflect the reality of the data model. We should move it to the UI logic.
Axis.prototype.getMotorCompatibility = function() {
  if (this.isNonMotorized()) {
    return false;
  }
  switch (this.getType()) {
    // Note Audrey : The telescopic column does have a motor.
    // But as it enclosed, the motor size is self-imposed an therefore the motor size dropdown hidden.
    // We could chose to show the dropdown as usual, but with only one option.
    case "telescopic_column":
      return false;
    default:
      return true;
  }
};

// CAD-ONLY protoype
// TODO: This prototype is just here for UI purposes and doesn't reflect the reality of the data model. We should move it to the UI logic.
Axis.prototype.getSensorCompatibility = function() {
  if (this.isNonMotorized()) {
    return false;
  }
  switch (this.getType()) {
    // Note Audrey : The telescopic column does have home/endstop sensors.
    // But as it enclosed, the sensors are comprised in the 3D model, as one part.
    // Therefore the home/end sensor dropdowns must be hidden.
    case "telescopic_column":
      return false;
    default:
      return true;
  }
};

Axis.prototype.getGearboxCompatibility = function(){
  return axisModel.isAxisTypeGearboxCompatible(this.getType());
};

Axis.prototype.getBrakeCompatability = function() {
  return axisModel.isAxisTypeBrakeCompatible(this.getType());
};

Axis.prototype.setCurrent = function(pValue) {
  this.mMotorCurrent = pValue;
  this.notify('onMotorCurrentChange', this.mMotorCurrent);
  this.setDirty();
};

Axis.prototype.getCurrent = function() {
  return this.mMotorCurrent;
};

/**
 * get the default motor current for the current motor size
 * @returns {number} the default motor current for the current motor size
 */
Axis.prototype.getDefaultMotorCurrent = function() {
  return axisModel.getMotorSettings(this.mMotorSize).motorCurrent.default;
};

/**
 * Sets the rotation of the axis.
 *
 * @param {number} pRotation
 */
Axis.prototype.setRotation = function(pRotation) {
  if (isNaN(pRotation)) {
    console.error('Cannot set rotation to: ' + pRotation);
    return;
  }

  this.mRotation = pRotation;
  this.setDirty();
};

/**
 * [WARNING]: This function is overridden in the assembler (vention_assembler/client/src/machinelogic_axis3d.js)
 * Swaps the rotation of the parent and child drives to the opposite of the current rotation.
 */
Axis.prototype.invertRotation = function() {
  this.mRotation = (-1) * this.mRotation;

  this.mChildrenDrives.forEach((child, index) => {
    child.rotation = (-1) * child.rotation;
  });

  this.setDirty();
};

/**
 * Gets the rotation of the axis.
 *
 * @returns {Number} Rotation of the axis.
 */
Axis.prototype.getRotation = function() {
  return this.mRotation;
};

/**
 * Sets homing speed of the axis.
 *
 * @param {Number} pHomingSpeed Homing speed of the axis.
 */
Axis.prototype.setHomingSpeed = function(pHomingSpeed) {
  if (isNaN(pHomingSpeed)) {
    console.error('Cannot set homing speed to: ' + pHomingSpeed);
    return;
  }

  this.mHomingSpeed = pHomingSpeed;
  this.setDirty();
  this.notify('onHomingSpeedChanged', this.mHomingSpeed);
};

/**
 * Gets the homing speed of the axis.
 *
 * @returns {Number} Homing speed of the axis.
 */
Axis.prototype.getHomingSpeed = function() {
  return this.mHomingSpeed;
};

Axis.prototype.getHomingSpeedInfo = function() {
  const lHomingSpeedInfo = axisModel.schemaMap.parameters.homingSpeed;
  const lAxisTypeInfo = axisModel.schemaMap.parameters.axisType;
  const isAxisTypeCustom = this.getType() === 'custom';
  const defaultHomingSpeed = lHomingSpeedInfo.axisTypeMapping[this.mType];
  const maxSpeed = lAxisTypeInfo.maxSpeedMapping[this.mType];
  return {
    min: lHomingSpeedInfo.minimum,
    max: maxSpeed,
    info: isAxisTypeCustom ? '' : "Default Value: " + defaultHomingSpeed,
    default: defaultHomingSpeed
  };
};

/**
 * get the default homing speed for the current axisType
 * @returns {number} the default homing speed for the current axisType
 */
Axis.prototype.getDefaultHomingSpeed = function() {
  return this.getHomingSpeedInfo().default;
};

Axis.prototype._resetHomingSpeedToDefault = function() {
  const lDefaultHomingSpeed = this.getDefaultHomingSpeed();
  this.setHomingSpeed(lDefaultHomingSpeed);
};

/**
 * Get proper axis JSON for each MM version
 */
Axis.prototype.toJson = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic_axis3d.js)
  let lPayload;

  if (this.isNonMotorized()) {
    lPayload = {
      category: 'config',
      name: 'pneumaticAxis',
      parameters: {
        uuid: this.mUUID,
        controllerId: this.mConfig.getController().getUUID(),
        friendlyName: this.mName,
        device: this.mDevice,
        pushOutPin: this.mPins.pushOutPin,
        pushInPin: this.mPins.pushInPin,
        pullOutPin: this.mPins.pullOutPin,
        pullInPin: this.mPins.pullInPin,
        axisType: this.getType(),
      }
    };
  } else {
    if (this.mConfig.getController().isHardwareV2()) {
      lPayload = {
        category: 'config',
        name: 'axis',
        parameters: {
          uuid: this.mUUID,
          controllerId: this.mConfig.getController().getUUID(),
          friendlyName: this.mName,
          axisColor: this.mColor,
          axisType: this.mType,
          mmPerRotation: this.mGain,
          multiplier: this.mMultiplier,
          tuningProfile: this.mTuningProfile,
          motorSize: this.mMotorSize,
          brake: this.mBrake,
          motorCurrent: this.mMotorCurrent,
          childrenDrives: this.mChildrenDrives,
          rotation: this.mRotation,
          homingSpeed: this.mHomingSpeed
        }
      };
    } else {
      lPayload = {
        category: 'config',
        name: 'axis',
        parameters: {
          uuid: this.mUUID,
          friendlyName: this.mName,
          axisColor: this.mColor,
          axisType: this.mType,
          mmPerRotation: this.mGain,
          multiplier: this.mMultiplier,
          brake: this.mBrake || 'none',
          // v1 has no children drives
        }
      };
    }
  }

  return lPayload;

};

Axis.prototype.fromJson = function(pAxisInstructions) {
  if(pAxisInstructions && pAxisInstructions.parameters) {
    this.mUUID = pAxisInstructions.parameters.uuid || uuidv4();
    this.mName = pAxisInstructions.parameters.friendlyName || this.mName;
    this.mColor = pAxisInstructions.parameters.axisColor || this.mColor;
    this.mType = pAxisInstructions.parameters.axisType || this.mType;
    this.mGain = pAxisInstructions.parameters.mmPerRotation || this.mGain;
    this.mMultiplier = pAxisInstructions.parameters.multiplier || this.mMultiplier;
    this.mTuningProfile = pAxisInstructions.parameters.tuningProfile || this.mTuningProfile;
    this.mMotorSize = pAxisInstructions.parameters.motorSize || this.mMotorSize;
    this.mBrake = pAxisInstructions.parameters.brake || this.mBrake;
    this.mMotorCurrent = pAxisInstructions.parameters.motorCurrent || this.mMotorCurrent;
    this.mRotation = pAxisInstructions.parameters.rotation || this.mRotation;
    this.mHomingSpeed = pAxisInstructions.parameters.homingSpeed || this.mHomingSpeed;
    this.mChildrenDrives = pAxisInstructions.parameters.childrenDrives || [];

    this.mDevice = pAxisInstructions.parameters.device || this.mDevice;
    this.mPins = {
      pushOutPin: Number.isInteger(pAxisInstructions.parameters.pushOutPin) ? pAxisInstructions.parameters.pushOutPin : this.mPins.pushOutPin,
      pushInPin: pAxisInstructions.parameters.pushInPin,
      pullOutPin: Number.isInteger(pAxisInstructions.parameters.pullOutPin) ? pAxisInstructions.parameters.pullOutPin : this.mPins.pullOutPin,
      pullInPin: pAxisInstructions.parameters.pullInPin,
    };
  }
};

/**
 * Get the mDevice property
 * @returns {number}
 */
Axis.prototype.getDevice = function () { return this.mDevice; };

/**
 * Set the mDevice for non-motorized actuator
 * @param {!number} pDevice device to set
 * @returns {void}
 */
Axis.prototype.setDevice = function (pDevice) {
  const lNextOutputParams = this.mConfig._nextNonMotorizedAxisOutputsByDevice(pDevice);
  const lNextInputParams = this.mConfig._nextNonMotorizedAxisInputsByDevice(pDevice);

  function assignPins(pThis){
    Object.keys(pThis.mPins).forEach(pKey => {
      if(pThis.mPins[pKey] !== undefined){
        if(/out/i.test(pKey)){
          pThis.setPin(pKey, lNextOutputParams[pKey]);
          return;
        }
        if( /in/i.test(pKey)){
          pThis.setPin(pKey, lNextInputParams[pKey]);
          return;
        }
      }
    });
  }

  // we can still use pneumatics if we dont have inputs
  if(lNextInputParams && lNextOutputParams.device !== lNextInputParams.device){
    // this should never happen as the unavailble device is grayed out and should be impossible to select
    // but if the disabled attribute is removed from the select, this will protect it.

    // the available devices are not the same so find the next available device.
    const lNextAvailableDevice = this.mConfig.nextNonMotorizedAxisInputsAndOutputs().device;
    // re-run this function with the correct device.
    this.setDevice(lNextAvailableDevice);
    return;
  } else {
    pDevice = lNextOutputParams.device;
    assignPins(this);
  }

  this.mDevice = pDevice;
  this.mConfig.updateActuatorIOMap();
  this.setDirty();
  this.notify('onDeviceChanged', this.mDevice);
};

/**
 * Get the value of one the mPins
 * @param {!string} pPinType Pins type (pullInPin|pullOutPin|pushInPin|pushOutPin)
 * @returns {number}
 */
Axis.prototype.getPin = function(pPinType) { return this.mPins[pPinType]; };

/**
 * Set value to one of the mPins
 * @param {!string} pPinType Pins type (pullInPin|pullOutPin|pushInPin|pushOutPin)
 * @param {!number} pValue Value (0-3)
 */
Axis.prototype.setPin = function(pPinType, pValue) {
  this.mPins[pPinType] = pValue != 'null' ? parseInt(pValue) : null;
  this.mConfig.updateActuatorIOMap();
  this.setDirty();
  this.notify('onPinChanged', pPinType, pValue);
};

Axis.prototype.getConfig = function() { return this.mConfig; };

Object.defineProperty(
  Axis.prototype,
  'name',
  {
    get: function () {
      return this.getName();
    }
  }
);


Object.defineProperty(
  Axis.prototype,
  'unit',
  {
    get: function () {
      return this.getUnits();
    }
  }
);

Object.defineProperty(
  Axis.prototype,
  'type',
  {
    get: function () {
      return this.getType();
    }
  }
);

